<template>
  <!-- Desktop Sidebar -->
  <div class="sidebar-container desktop">
    <div class="logo-section">
      <img :src="sidebarData?.logo || ''" alt="AssetNivesh Logo" class="logo-img" />
    </div>

    <nav class="nav-section">
      <div
        v-for="(item, key) in sidebarData?.listData || {}"
        :key="key"
        @click="item.disabled ? null : handleNavClick(key, item)"
        :class="[
          'nav-item',
          { 'nav-item-active': store?.userTab === key },
          { 'nav-item-disabled': item.disabled || isAnyLoading }
        ]"
      >
        <div class="nav-content">
          <component v-if="item?.icon" :is="item.icon" class="nav-icon" />
          <span class="nav-label">{{ item?.label || '' }}</span>
        </div>
      </div>
    </nav>

    <div class="footer-section">
      <div class="logout-container">
        <AnButton
          label="Logout"
          variant="text"
          severity="danger"
          size="small"
          :loading="store?.isSignoutLoading || false"
          :disabled="isAnyLoading && !store?.isSignoutLoading"
          @click="handleDesktopLogout"
          class="logout-button"
        />
      </div>
      <div class="footer-links">
        <router-link to="/sebi-disclosures" class="footer-link sebi-link">
          <strong>SEBI IA Disclosures</strong>
        </router-link>
      </div>
    </div>
  </div>

  <!-- Mobile Header with Hamburger -->
  <div class="tablet header w-fs bg-white pv-10 ph-20 d-flex justify-between align-items-center">
    <img :src="sidebarData?.logo || ''" alt="AssetNivesh Logo" class="an-logo-container" />
    <i
      class="pi pi-bars tablet bg-primary text-white br-5 p-10 cursor-pointer"
      @click="showMenu = !showMenu"
    />
  </div>

  <!-- Mobile Overlay Menu (replacing Drawer) -->
  <div v-if="showMenu" class="mobile-overlay tablet" @click="closeMenu">
    <div class="mobile-menu" @click.stop>
      <div class="mobile-header">
        <div class="d-flex align-items-center">
          <div class="bg-light p-30 w-max h-max br-round position-relative">
            <i class="pi pi-user dead-center h-max w-max font-20" />
          </div>
          <div class="d-flex flex-direction-column ml-10">
            <span class="font-weight-700">Hi, {{ user?.username || 'User' }}</span>
          </div>
        </div>
        <i class="pi pi-times close-icon" @click="closeMenu" />
      </div>

      <nav class="mobile-nav">
        <div
          v-for="(item, key) in sidebarData?.listData || {}"
          :key="key"
          class="mobile-nav-item"
          :class="{
            'mobile-nav-item-active': store?.userTab === key,
            'mobile-nav-item-disabled': item.disabled || isAnyLoading,
          }"
          @click="item.disabled ? null : handleMobileNavClick(key, item)"
        >
          <component v-if="item?.icon" :is="item.icon" :color="getIconColor(key)" class="mobile-nav-icon" />
          <span class="mobile-nav-label">{{ item?.label || '' }}</span>
        </div>
      </nav>

      <div class="mobile-footer">
        <AnButton
          @click="handleMobileLogout"
          label="Logout"
          severity="danger"
          class="mobile-logout-btn"
          icon="pi pi-sign-out"
          :loading="store?.isSignoutLoading || false"
          :disabled="isAnyLoading && !store?.isSignoutLoading"
        />
        <div class="mobile-footer-links">
          <router-link to="/sebi-disclosures" class="mobile-footer-link sebi-link" @click="closeMenu">
            <strong>SEBI IA Disclosures</strong>
          </router-link>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, ref, nextTick, onErrorCaptured, watch } from "vue";
import { useUserStore } from "@/store/user-store";
import { getFilteredSidebarData } from "@/services/constant-services";
import AnButton from "@/components/common/AnButton.vue";

// Add error boundary
onErrorCaptured((err, instance, info) => {
  console.error('SidebarNavigation error captured:', err, info);
  // Return false to prevent the error from propagating further
  return false;
});

const store = useUserStore();
const user = computed(() => {
  try {
    return store.getuserData || null;
  } catch (error) {
    console.error('Error getting user data:', error);
    return null;
  }
});
const showMenu = ref(false);

// Track payment status changes to force sidebar refresh
const paymentStatus = computed(() => store.dashboardData?.paymentStatus);

// Make sidebar data reactive to store changes with better error handling
const sidebarData = computed(() => {
  try {
    // Access paymentStatus to create dependency
    const currentPaymentStatus = paymentStatus.value;
    console.log('Sidebar recomputing with payment status:', currentPaymentStatus);

    const filteredData = getFilteredSidebarData();
    // Ensure we always have valid data structure
    return filteredData || { logo: '', listData: {} };
  } catch (error) {
    console.error('Error getting sidebar data:', error);
    // Return a safe default structure
    return {
      logo: '/assets/logo.png', // Default logo path
      listData: {
        Dashboard: { label: 'Dashboard', icon: 'pi pi-home' }
      }
    };
  }
});

// Watch payment status changes and log sidebar updates
watch(paymentStatus, (newStatus, oldStatus) => {
  console.log('Payment status changed in sidebar:', { oldStatus, newStatus });
  // console.log('Sidebar items after status change:', Object.keys(sidebarData.value.listData || {}));
});

// Check if any loading state is active
const isAnyLoading = computed(() => {
  try {
    return store.isDashboardLoading ||
           store.isAuthLoading ||
           store.isSignoutLoading ||
           store.isRiskAssessmentLoading ||
           store.isProfileCheckLoading; // Include profile check loading
  } catch (error) {
    console.error('Error checking loading state:', error);
    return false;
  }
});

const getIconColor = (key) => {
  try {
    return store.userTab === key ? "var(--an-primary)" : "var(--an-gray)";
  } catch (error) {
    console.error('Error getting icon color:', error);
    return "var(--an-gray)";
  }
};

const handleNavClick = (key, item) => {
  try {
    // Prevent navigation if any loading state is active
    if (isAnyLoading.value) {
      console.log('Navigation blocked: loading in progress');
      return;
    }

    // Execute any action if defined
    if (item && item.action && typeof item.action === 'function') {
      item.action();
    }

    // Set the active tab
    if (store && typeof store.setTabView === 'function') {
      store.setTabView(key);
    }
  } catch (error) {
    console.error('Error in handleNavClick:', error);
  }
};

const handleMobileNavClick = (key, item) => {
  try {
    // Prevent navigation if any loading state is active
    if (isAnyLoading.value) {
      console.log('Mobile navigation blocked: loading in progress');
      return;
    }

    // Execute any action if defined
    if (item && item.action && typeof item.action === 'function') {
      item.action();
    }

    // Set the active tab
    if (store && typeof store.setTabView === 'function') {
      store.setTabView(key);
    }

    // Close the mobile menu
    showMenu.value = false;
  } catch (error) {
    console.error('Error in handleMobileNavClick:', error);
    // Always try to close menu even on error
    showMenu.value = false;
  }
};

const handleDesktopLogout = async () => {
  try {
    // Prevent multiple logout attempts
    if (store.isSignoutLoading) return;

    await store.handleLogout();
  } catch (error) {
    console.error('Desktop logout error:', error);
  }
};

const handleMobileLogout = async () => {
  try {
    // Prevent multiple logout attempts
    if (store.isSignoutLoading) return;

    // Close mobile menu immediately
    showMenu.value = false;

    // Wait for DOM updates to complete
    await nextTick();

    // Small delay to ensure all reactive updates are processed
    await new Promise(resolve => setTimeout(resolve, 50));

    // Execute logout
    await store.handleLogout();
  } catch (error) {
    console.error('Mobile logout error:', error);
    // Reset state on error
    showMenu.value = false;
  }
};

const closeMenu = () => {
  showMenu.value = false;
};
</script>

<style scoped>
/* Desktop Sidebar - Only show on desktop */
.sidebar-container.desktop {
  width: 280px;
  height: 100vh;
  background: #f8f9fa;
  border-right: 1px solid #dee2e6;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  position: relative;
  box-shadow: 2px 0 8px rgba(0, 0, 0, 0.1);
}

.logo-section {
  padding: 24px 20px;
  border-bottom: 1px solid #dee2e6;
  flex-shrink: 0;
  background: #ffffff;
}

.logo-img {
  height: 36px;
  width: auto;
}

.nav-section {
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 16px 0 20px 0;
  background: #f8f9fa;
  position: relative;
  scrollbar-width: thin;
  scrollbar-color: #ced4da transparent;
}

/* Custom scrollbar styling */
.nav-section::-webkit-scrollbar {
  width: 8px;
}

.nav-section::-webkit-scrollbar-track {
  background: transparent;
  border-radius: 10px;
}

.nav-section::-webkit-scrollbar-thumb {
  background: linear-gradient(180deg, #e0e0e0, #c0c0c0);
  border-radius: 10px;
  border: 2px solid transparent;
  background-clip: content-box;
}

.nav-section::-webkit-scrollbar-thumb:hover {
  background: linear-gradient(180deg, #c0c0c0, #a0a0a0);
}

.nav-item {
  margin: 4px 16px;
  padding: 12px 16px;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  min-height: 48px;
  background: transparent;
}

.nav-item:hover {
  background-color: #e9ecef;
}

.nav-item-disabled {
  opacity: 0.5;
  cursor: not-allowed !important;
  pointer-events: none;
}

.nav-item-disabled:hover {
  background-color: transparent !important;
}

.nav-item-active {
  background-color: #d4edda;
  color: #155724;
  border: 1px solid #c3e6cb;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.nav-item-active .nav-label {
  color: #155724;
  font-weight: 600;
}

.nav-item-active .nav-icon {
  color: #155724;
}

.nav-content {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
}

.nav-icon {
  width: 18px;
  height: 18px;
  color: #6b7280;
  transition: color 0.2s ease;
  flex-shrink: 0;
}

.nav-label {
  color: #374151;
  font-size: 14px;
  font-weight: 500;
  transition: color 0.2s ease;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.footer-section {
  padding: 0 20px 20px 20px;
  border-top: 1px solid #dee2e6;
  flex-shrink: 0;
  background: #ffffff;
}

.logout-container {
  width: 100%;
  margin-top: 8px;
}

.footer-links {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 4px;
  padding-top: 4px;
  padding-bottom: 4px;
  border-top: 1px solid #e5e7eb;
}

.footer-link {
  font-size: 0.75rem;
  color: #6b7280;
  text-decoration: none;
  transition: color 0.2s ease;
}

.footer-link:hover {
  color: #155724;
  text-decoration: underline;
}

.footer-link.sebi-link {
  color: #155724;
  font-weight: 700;
}

.logout-button {
  width: 100%;
  padding: 12px 16px;
  background: #dc3545;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  transition: all 0.2s ease;
}

.logout-button:hover {
  background: #c82333;
  transform: translateY(-1px);
  box-shadow: 0 2px 4px rgba(220, 53, 69, 0.2);
}

.logout-button:active {
  transform: translateY(0);
}

/* Mobile Header - Only show on tablet/mobile */
.header.tablet {
  display: none;
  height: 80px;
  border-bottom: 1px solid #e5e7eb;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.an-logo-container {
  height: 50px;
}

/* Mobile Overlay Menu Styles */
.mobile-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 1000;
  display: flex;
  justify-content: flex-end;
}

.mobile-menu {
  width: 300px;
  height: 100%;
  background: white;
  box-shadow: -2px 0 8px rgba(0, 0, 0, 0.1);
  display: flex;
  flex-direction: column;
  animation: slideInRight 0.3s ease-out;
}

@keyframes slideInRight {
  from {
    transform: translateX(100%);
  }
  to {
    transform: translateX(0);
  }
}

.mobile-header {
  padding: 20px;
  border-bottom: 1px solid #e5e7eb;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.close-icon {
  cursor: pointer;
  padding: 8px;
  color: #6b7280;
  font-size: 18px;
}

.close-icon:hover {
  color: #374151;
  background-color: #f3f4f6;
  border-radius: 4px;
}

.mobile-nav {
  flex: 1;
  overflow-y: auto;
  padding: 16px 0;
}

.mobile-nav-item {
  display: flex;
  align-items: center;
  padding: 12px 20px;
  margin: 2px 12px;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  gap: 12px;
}

.mobile-nav-item:hover {
  background-color: #f3f4f6;
}

.mobile-nav-item-active {
  background-color: #e0f7fa;
  color: #006064;
  border-left: 3px solid #006064;
  margin-left: 9px;
  padding-left: 17px;
}

.mobile-nav-item-disabled {
  opacity: 0.5;
  pointer-events: none;
}

.mobile-nav-icon {
  width: 18px;
  height: 18px;
  flex-shrink: 0;
}

.mobile-nav-label {
  font-size: 14px;
  font-weight: 500;
}

.mobile-footer {
  padding: 16px 20px;
  border-top: 1px solid #e5e7eb;
}

.mobile-logout-btn {
  width: 100%;
  padding: 12px 16px;
}

.mobile-footer-links {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 4px;
  padding-top: 4px;
  border-top: 1px solid #e5e7eb;
}

.mobile-footer-link {
  font-size: 0.75rem;
  color: #6b7280;
  text-decoration: none;
  text-align: center;
  padding: 4px 0;
  transition: color 0.2s ease;
}

.mobile-footer-link:hover {
  color: #006064;
  text-decoration: underline;
}

.mobile-footer-link.sebi-link {
  color: #006064;
  font-weight: 700;
}

/* Mobile Drawer Styles */
.list-wrapper {
  overflow-y: auto;
  padding: 10px 0;
}

.list-item {
  color: var(--an-gray);
  transition: all 0.2s ease;
}

.list-item:active {
  background-color: var(--an-background-light);
  color: var(--an-primary);
}

.list-item:hover {
  background-color: var(--an-background-light);
}

.item-selected {
  background-color: var(--an-background-light);
  color: var(--an-primary);
}

.item-selected::before {
  content: "";
  height: 50px;
  width: 50px;
  background: var(--an-primary);
  display: flex;
  position: absolute;
  left: -65px;
  transform: rotate(45deg);
  top: 0;
  bottom: 0;
  border-radius: 10px;
}

.item-disabled {
  pointer-events: none;
  opacity: 0.5;
}

/* Responsive breakpoints */
@media (max-width: 1200px) {
  /* Hide desktop sidebar */
  .sidebar-container.desktop {
    display: none !important;
  }

  /* Show mobile header */
  .header.tablet {
    display: flex !important;
  }
}

@media (min-width: 1201px) {
  /* Hide mobile header on desktop */
  .header.tablet {
    display: none !important;
  }

  /* Show desktop sidebar */
  .sidebar-container.desktop {
    display: flex !important;
  }
}

/* Smooth scrolling */
html {
  scroll-behavior: smooth;
}

/* Focus states for accessibility */
.nav-item:focus {
  outline: 2px solid #006064;
  outline-offset: 2px;
}

.logout-button:focus {
  outline: 2px solid #ffffff;
  outline-offset: 2px;
}

.list-item:focus {
  outline: 2px solid var(--an-primary);
  outline-offset: 2px;
}

/* Utility classes for responsive visibility */
.desktop {
  display: block;
}

.tablet {
  display: none;
}

@media (max-width: 1200px) {
  .desktop {
    display: none !important;
  }

  .tablet {
    display: block;
  }
}
</style>
